"use client";

// Keeps the signed-in user's email hero fresh. The organism can only be rendered
// where WebGL lives — the browser — so the dashboard bakes the user's REAL bloom
// and uploads it; the email routes then serve THEIR organism, not the default
// netti. The bake uses the SAME derivation as the bench and the launch email —
// futureIdentity(email, cells): seed (form) + vivid colour + memory age — on the
// saturated organism-hue backdrop, with their avatar + username. Runs rarely: the
// hidden offscreen engine only mounts when a fresh bake is due (per-day, or when
// their bloom / the design changes). Entirely best-effort; silent.

import { useEffect, useRef, useState } from "react";
import MemoryOrganism, { type OrganismApi } from "../MemoryOrganism";
import { futureIdentity } from "../../lib/organism/future/identity";
import { identityKey } from "../../lib/organism/identity";
import { bakeHeroCanvas, canvasToBlob, heroBgRgb } from "../../lib/brand/bakeHero";
import { bakeGroundSheet } from "../../lib/brand/groundSheet";

const DAY = 86_400_000;
const STAMP_KEY = "innernet:hero-baked";
// bump when the hero DESIGN changes so every user re-bakes on next dashboard load.
// 4: the planted bloom became a ground sheet (lib/organism/groundLaw).
const HERO_BAKE_VERSION = 4;
// each sheet frame's width — the email crest plants ~520px of it at 2×, the
// MCP corner ~216px; five frames stay well under the upload cap
const SHEET_FRAME_W = 420;
// what each render is actually used at, at 2× — never the engine's 2048
// default. Every export is a synchronous GPU readback + PNG encode on the main
// thread and costs the SQUARE of this: at 2048 the five-ground sheet froze the
// dashboard for seconds (the 2026-09-27 "clicks do nothing" hang).
const HERO_PX = 1280; // the composite plants the bloom ~1.5× an 806px-tall frame
const SHEET_PX = SHEET_FRAME_W * 2;
// how long the page must have been left alone before a bake may start
const QUIET_MS = 4000;

/** resolve once nobody has touched the page for QUIET_MS AND the browser is
 *  idle — a bake is never allowed to compete with someone using the dashboard */
function whenQuiet(signal: { cancelled: boolean }): Promise<void> {
  return new Promise((done) => {
    let last = performance.now();
    const touch = () => { last = performance.now(); };
    const evs = ["pointerdown", "pointermove", "keydown", "wheel", "scroll", "touchstart"] as const;
    for (const e of evs) window.addEventListener(e, touch, { passive: true, capture: true });
    const finish = () => { for (const e of evs) window.removeEventListener(e, touch, true); done(); };
    const check = () => {
      if (signal.cancelled) return finish();
      if (performance.now() - last < QUIET_MS || document.visibilityState !== "visible") { setTimeout(check, 1000); return; }
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      if (ric) ric(() => (performance.now() - last < QUIET_MS ? check() : finish()), { timeout: 3000 });
      else finish();
    };
    setTimeout(check, QUIET_MS);
  });
}
/** hand the main thread back for a frame between two heavy steps */
const breathe = () => new Promise<void>((r) => requestAnimationFrame(() => setTimeout(r, 0)));

export default function HeroUploader({ email, cells }: { email: string; cells: number }) {
  const apiRef = useRef<OrganismApi | null>(null);
  const [bake, setBake] = useState(false);

  const ident = futureIdentity(email, cells);
  const username = identityKey(email);
  // the saturated hero field — the hero composite IS painted on it, so it bakes
  // native; the sheet below carries every other ground
  const bg = heroBgRgb(ident.color);
  // what the hero depends on — bloom colour + form seed + age band + design
  // version. Any of these changing re-bakes; nothing else. (The user's ground is
  // carried by the email PAGE, not the hero.)
  const sig = `${email}:v${HERO_BAKE_VERSION}:${ident.seed}:${Math.round(ident.age * 20)}:${Math.round(ident.color.primary.hue)}`;

  useEffect(() => {
    let due = true;
    try {
      const raw = localStorage.getItem(STAMP_KEY);
      if (raw) {
        const { at, sig: prev } = JSON.parse(raw);
        due = prev !== sig || Date.now() - at > DAY;
      }
    } catch {
      /* no storage → just bake */
    }
    if (!due) return;
    // the hidden engine (a second live WebGL netti) mounts only once the page
    // has been left alone — never while someone is using the dashboard
    const signal = { cancelled: false };
    void whenQuiet(signal).then(() => { if (!signal.cancelled) setBake(true); });
    return () => { signal.cancelled = true; };
  }, [sig]);

  useEffect(() => {
    if (!bake) return;
    const signal = { cancelled: false };
    void (async () => {
      // let the freshly mounted engine paint a few frames first
      await new Promise((r) => setTimeout(r, 700));
      if (signal.cancelled) return;
      // stamp the ATTEMPT, not only a success: a bake whose upload fails used
      // to re-run (and re-freeze the page) on every single load
      try { localStorage.setItem(STAMP_KEY, JSON.stringify({ at: Date.now(), sig })); } catch { /* no storage */ }
      try {
        // 1. the composited email hero (bloom + field + username) — for emails
        const cv = await bakeHeroCanvas({
          exportPng: () => apiRef.current?.exportPng(HERO_PX) ?? "",
          color: ident.color,
          username,
        });
        const blob = await canvasToBlob(cv);
        if (signal.cancelled || !blob) return;
        const res = await fetch("/api/me/hero", { method: "POST", headers: { "Content-Type": "image/png" }, body: blob });

        // 2. the GROUND SHEET — the netti that gets planted into surfaces with no
        // field around it (the email band, the banner, the MCP App corner, the
        // cards). Not one frame: a frame frozen on this ground washes out on a
        // pale one and goes muddy on a dark one, so it ships painted at every
        // stop of the ground law and each surface reads the look for its own
        // ground (lib/organism/groundLaw · lib/brand/groundTrue). Best-effort;
        // a failure here never blocks the hero.
        try {
          await breathe();
          if (signal.cancelled) return;
          const sheet = await bakeGroundSheet((d) => apiRef.current?.exportPngs(d, SHEET_PX) ?? d.map(() => ""), SHEET_FRAME_W);
          const sheetBlob = sheet ? await canvasToBlob(sheet) : null;
          if (sheetBlob && !signal.cancelled) {
            await fetch("/api/me/hero?variant=sheet", {
              method: "POST",
              headers: { "Content-Type": "image/png" },
              body: sheetBlob,
            });
          }
        } catch {
          /* the planted netti is a nicety — never fail the hero over it */
        }

        void res;
      } catch {
        /* best-effort — never surface */
      } finally {
        if (!signal.cancelled) setBake(false);
      }
    })();
    return () => {
      signal.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bake]);

  if (!bake) return null;
  return (
    <div aria-hidden style={{ position: "fixed", left: -9999, top: -9999, width: 560, height: 403, pointerEvents: "none", opacity: 0 }}>
      <MemoryOrganism
        apiRef={apiRef}
        seed={ident.seed}
        color={ident.color}
        bg={bg}
        age={ident.age}
        spin={false}
        grow={false}
        live={false}
        publishHue={false}
        renderScale={1}
      />
    </div>
  );
}
